/**
 * W3 Rotator
 *
 * Shows N slides (N comes from the --w3r-per-view CSS variable), advances
 * one slide at a time, and wraps. Rotation reorders the real DOM instead of
 * cloning slides, so the first N slides in the markup are always the ones on
 * screen. That keeps tab order and screen reader order identical to what a
 * sighted user sees: every slide past N is marked inert.
 *
 * Only rotates when there are more slides than fit on screen. With 4 slides,
 * desktop (4 per view) is a static row and mobile (2 per view) rotates.
 */
(() => {
  if (customElements.get('w3-rotator')) return;

  const DURATION = 400;
  const EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)';
  const SWIPE_THRESHOLD = 40;
  const CLICK_SUPPRESS_MS = 300;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class W3Rotator extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.enabled = false;
      this.pendingFinish = null;
      this.fallbackTimer = null;
      this.pointerStart = null;
      this.suppressClickUntil = 0;

      this.onPrev = () => this.move(-1);
      this.onNext = () => this.move(1);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onPointerCancel = () => { this.pointerStart = null; };
      this.onClickCapture = this.handleClickCapture.bind(this);
      this.onDragStart = (event) => event.preventDefault();
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-w3r-viewport]');
      this.track = this.querySelector('[data-w3r-track]');
      this.prevButton = this.querySelector('[data-w3r-prev]');
      this.nextButton = this.querySelector('[data-w3r-next]');
      if (!this.viewport || !this.track) return;

      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerCancel);
      this.viewport.addEventListener('click', this.onClickCapture, true);
      // Native link/image dragging would swallow the pointer events we swipe with.
      this.viewport.addEventListener('dragstart', this.onDragStart);
      // Theme editor: bring a selected block into view.
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      // Re-checks per-view count on breakpoint changes. Fires once on observe.
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.viewport);
      this.update();
    }

    disconnectedCallback() {
      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerCancel);
      this.viewport?.removeEventListener('click', this.onClickCapture, true);
      this.viewport?.removeEventListener('dragstart', this.onDragStart);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.resizeObserver?.disconnect();
      clearTimeout(this.fallbackTimer);
    }

    get slides() {
      return Array.from(this.track.children);
    }

    get perView() {
      const value = parseInt(getComputedStyle(this).getPropertyValue('--w3r-per-view'), 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    // Distance between the left edges of two neighbouring slides (width + gap).
    // Uses bounding rects for sub-pixel accuracy so the reset after each move
    // doesn't snap by a fraction of a pixel.
    get step() {
      const [first, second] = this.track.children;
      if (!first || !second) return 0;
      return second.getBoundingClientRect().left - first.getBoundingClientRect().left;
    }

    update() {
      this.enabled = this.slides.length > this.perView;
      this.toggleAttribute('data-enabled', this.enabled);
      this.syncInert();
    }

    syncInert() {
      const perView = this.perView;
      this.slides.forEach((slide, index) => {
        slide.inert = index >= perView;
      });
    }

    move(direction) {
      if (!this.enabled || this.busy) return;
      const step = this.step;
      if (!step) return;

      // No animation: rotate instantly. Also avoids waiting on a
      // transitionend that never fires when transitions are disabled.
      if (reducedMotion.matches) {
        if (direction > 0) this.track.append(this.track.firstElementChild);
        else this.track.prepend(this.track.lastElementChild);
        this.syncInert();
        return;
      }

      this.busy = true;

      if (direction > 0) {
        // Slide left by one, then move the first slide to the end and reset.
        this.animateTo(-step, () => {
          this.track.append(this.track.firstElementChild);
          this.syncInert();
        });
      } else {
        // Move the last slide to the front, offset by one without animating,
        // then animate back to zero.
        this.track.prepend(this.track.lastElementChild);
        this.syncInert();
        this.jumpTo(-step);
        this.track.getBoundingClientRect(); // commit the offset before animating
        this.animateTo(0);
      }
    }

    animateTo(x, beforeReset) {
      let finished = false;

      const onEnd = (event) => {
        if (event.target === this.track && event.propertyName === 'transform') finish();
      };

      const finish = () => {
        if (finished) return;
        finished = true;
        this.track.removeEventListener('transitionend', onEnd);
        clearTimeout(this.fallbackTimer);
        this.pendingFinish = null;
        if (beforeReset) beforeReset();
        this.jumpTo(0);
        this.busy = false;
      };

      this.pendingFinish = finish;
      this.track.addEventListener('transitionend', onEnd);
      // Safety net in case transitionend doesn't fire (background tab, etc.).
      this.fallbackTimer = setTimeout(finish, DURATION + 100);

      this.track.style.transition = `transform ${DURATION}ms ${EASING}`;
      this.track.style.transform = `translate3d(${x}px, 0, 0)`;
    }

    jumpTo(x) {
      this.track.style.transition = 'none';
      this.track.style.transform = x ? `translate3d(${x}px, 0, 0)` : '';
    }

    handleBlockSelect(event) {
      if (!this.enabled || !this.contains(event.target)) return;
      const slide = event.target.closest('[data-w3r-slide]');
      if (!slide) return;

      // Complete any in-flight move so it can't reorder slides afterwards.
      if (this.pendingFinish) this.pendingFinish();

      let guard = this.slides.length;
      while (this.slides.indexOf(slide) >= this.perView && guard > 0) {
        this.track.append(this.track.firstElementChild);
        guard -= 1;
      }
      this.syncInert();
    }

    handlePointerDown(event) {
      if (!this.enabled || !event.isPrimary || event.button !== 0) return;
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }

    handlePointerUp(event) {
      if (!this.pointerStart) return;
      const dx = event.clientX - this.pointerStart.x;
      const dy = event.clientY - this.pointerStart.y;
      this.pointerStart = null;

      if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;

      // A mouse drag ends in a click on the slide; don't follow the link.
      this.suppressClickUntil = performance.now() + CLICK_SUPPRESS_MS;
      this.move(dx < 0 ? 1 : -1);
    }

    handleClickCapture(event) {
      if (performance.now() < this.suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        this.suppressClickUntil = 0;
      }
    }
  }

  customElements.define('w3-rotator', W3Rotator);
})();